import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search } from "lucide-react";
import { Popover, PopoverContent } from "./ui/popover";
import { PopoverTrigger } from "@radix-ui/react-popover";
import agent from "@/lib/public-atp-agent";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { Link } from "react-router";

interface SearchInputProps {
  onSearch: (handle: string) => void;
}

export const SearchInput = ({ onSearch }: SearchInputProps) => {
  const [handle, setHandle] = useState("");
  const debouncedQuery = useDebounce(handle, 100);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { data: typeaheadResponse, isFetching: isLoading } = useQuery({
    queryKey: ["actors", { debouncedQuery }],
    queryFn: async () => {
      const user = await agent.searchActorsTypeahead({ q: debouncedQuery, limit: 5 });
      return user;
    },
    enabled: handle.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 100,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const handle = e.target.value;
    setHandle(handle);
  }

  const handleSearch = () => {
    onSearch(handle);
  }

  useEffect(() => {
    if (isLoading || (typeaheadResponse?.success && typeaheadResponse?.data.actors.length > 0)) {
      setPopoverOpen(true);
    } else {
      setPopoverOpen(false);
    }
  }, [typeaheadResponse, isLoading, popoverOpen]);

  const ref = useRef<HTMLInputElement>(null);

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={(o) => setPopoverOpen(o)}>
        <div className="relative w-full max-w-lg mx-auto">
          <PopoverTrigger asChild className="w-full" onFocusCapture={(e) => e.preventDefault()}>
            <div className="relative">
              <Input
                ref={ref}
                autoFocus
                type="text"
                placeholder="Start by typing a handle or username..."
                prefix="@"
                value={handle}
                onChange={handleChange}
                className="pl-11 pr-16 py-8 md:text-lg w-full rounded-xl border-2 border-gray-200 focus:border-primary/50 transition-colors"
              />
              <span className="absolute top-1/2 left-4 -translate-y-1/2 text-primary text-2xl">
                @
              </span>
              {/*<Button
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md h-12 w-12"
                variant="secondary"
                size="icon"
                onClick={handleSearch}
              >
                <Search className="h-8 w-8 text-primary" />
              </Button>*/}
            </div>
          </PopoverTrigger>
        </div>

        <PopoverContent side="bottom" align="start" className="min-w-2 p-1" onOpenAutoFocus={(e) => e.preventDefault()} >
          {isLoading && (<div className="flex items-center gap-2 p-2 animate-pulse text-secondary-foreground text-sm">
            <Loader2 className="animate-spin" />
            Loading...
          </div>)
          }
          {!isLoading && <div className="flex flex-col animate-fade-in">
            {typeaheadResponse?.data.actors.map((actor) => (
              <div onClick={() => { onSearch(actor.handle); setHandle(""); }} role="link" key={actor.handle} className="flex md:flex-col gap-2 md:gap-0 p-1 md:p-2 rounded-sm md:rounded-md hover:bg-muted cursor-pointer">
                <span className="text-sm truncate">{actor.displayName || actor.handle}</span>
                <span className="text-sm text-muted-foreground truncate">@{actor.handle}</span>
              </div>
            ))}
          </div>}
        </PopoverContent>
      </Popover>
    </>
  );
};

